import React from 'react'
import { Box, Button, CircularProgress } from '@mui/material'

import './prep-grid.css'

const isDevEnvironment = process.env.NODE_ENV !== 'production'

let hasLoggedGridItemSize = false
let hasLoggedGridTemplateColumns = false

const useGridItemDevGuard = (debugLabel?: string) => {
  const itemRef = React.useRef<HTMLDivElement | null>(null)
  const [isNarrow, setIsNarrow] = React.useState(false)

  React.useEffect(() => {
    if (!isDevEnvironment) {
      return
    }

    const node = itemRef.current
    if (!node) {
      return
    }

    const updateSize = (width: number, height: number) => {
      setIsNarrow(width < 200)

      const roundedWidth = Math.round(width)
      node.dataset.prepGridWidth = Number.isFinite(roundedWidth) ? String(roundedWidth) : ''
      if (width < 200) {
        node.dataset.prepGridNarrow = 'true'
      } else {
        delete node.dataset.prepGridNarrow
      }

      if (!hasLoggedGridItemSize) {
        hasLoggedGridItemSize = true
        if (typeof console !== 'undefined') {
          console.debug(`[${debugLabel ?? 'prep-grid'}] grid item size`, { width, height })
        }
      }
    }

    const rect = node.getBoundingClientRect()
    updateSize(rect.width, rect.height)

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const { width, height } = entry.contentRect
          updateSize(width, height)
        })
      })

      observer.observe(node)

      return () => {
        observer.disconnect()
        delete node.dataset.prepGridWidth
        delete node.dataset.prepGridNarrow
      }
    }

    return () => {
      delete node.dataset.prepGridWidth
      delete node.dataset.prepGridNarrow
    }
  }, [debugLabel])

  return { itemRef, isNarrow }
}

interface PrepGridItemContainerProps {
  children: React.ReactNode
  debugLabel?: string
}

const PrepGridItemContainer: React.FC<PrepGridItemContainerProps> = ({ children, debugLabel }) => {
  const { itemRef, isNarrow } = useGridItemDevGuard(debugLabel)

  return (
    <Box
      ref={itemRef}
      className="prep-grid-item"
      sx={{
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        ...(isDevEnvironment && isNarrow
          ? {
              outline: (theme) => `1px dashed ${theme.palette.error.main}`,
            }
          : null),
      }}
    >
      {children}
    </Box>
  )
}

export interface PrepGridProps<T> {
  items: readonly T[]
  renderItem: (item: T) => React.ReactNode
  getKey?: (item: T, index: number) => React.Key
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  scrollContainer?: HTMLDivElement | null
  emptyState?: React.ReactNode
  debugLabel?: string
  forceSingleColumn?: boolean
}

const PrepGrid = <T,>({
  items,
  renderItem,
  getKey,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  scrollContainer,
  emptyState = null,
  debugLabel,
  forceSingleColumn = false,
}: PrepGridProps<T>) => {
  const ioSentinelRef = React.useRef<HTMLDivElement | null>(null)
  const gridRef = React.useRef<HTMLDivElement | null>(null)
  const loadMoreRef = React.useRef(onLoadMore)
  loadMoreRef.current = onLoadMore

  const intersectionRoot = scrollContainer ?? null

  React.useEffect(() => {
    if (!isDevEnvironment || hasLoggedGridTemplateColumns) {
      return
    }

    const node = gridRef.current
    if (!node) {
      return
    }

    try {
      if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
        return
      }

      const style = window.getComputedStyle(node)
      const value = style?.gridTemplateColumns
      if (typeof console !== 'undefined') {
        console.debug(`[${debugLabel ?? 'prep-grid'}] grid template columns`, value)
      }
      hasLoggedGridTemplateColumns = true
    } catch (error) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('Failed to read computed grid template columns', error)
      }
    }
  }, [debugLabel])

  React.useEffect(() => {
    const sentinel = ioSentinelRef.current
    if (!sentinel || !hasMore || !loadMoreRef.current) {
      return
    }

    let ignore = false

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!ignore && entry.isIntersecting && loadMoreRef.current && !loadingMore) {
            loadMoreRef.current()
          }
        })
      },
      { root: intersectionRoot, rootMargin: '200px 0px', threshold: 0.01 },
    )

    observer.observe(sentinel)

    return () => {
      ignore = true
      observer.disconnect()
    }
  }, [intersectionRoot, hasMore, loadingMore])

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: { xs: 2, md: 3 },
        py: 2,
      }}
    >
      <Box
        data-prep-measure
        aria-hidden
        sx={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      />
      <Box
        ref={gridRef}
        id="prep-grid"
        sx={{
          display: 'grid',
          gridAutoFlow: 'row',
          gap: 2,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          gridTemplateColumns: forceSingleColumn
            ? 'minmax(280px, 1fr)'
            : {
                xs: 'repeat(auto-fit, minmax(300px, 1fr))',
                md: 'repeat(auto-fit, minmax(340px, 1fr))',
                lg: 'repeat(auto-fit, minmax(360px, 1fr))',
              },
          alignItems: 'stretch',
          justifyItems: 'stretch',
          alignContent: 'start',
          overflowX: 'hidden',
        }}
      >
        {items.map((item, index) => (
          <PrepGridItemContainer key={getKey ? getKey(item, index) : index} debugLabel={debugLabel}>
            {renderItem(item)}
          </PrepGridItemContainer>
        ))}
      </Box>

      {items.length === 0 && emptyState ? emptyState : null}

      <Box
        ref={ioSentinelRef}
        id="prep-io-sentinel"
        data-prep-sentinel
        aria-hidden
        sx={{
          height: 1,
          mt: hasMore ? 2 : 0,
        }}
      />

      {hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: { xs: 2, md: 3 }, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={18} /> : null}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

export default React.memo(PrepGrid) as typeof PrepGrid
